import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import DataFlowIcon from '@atlaskit/icon/core/data-flow';
import DataStringIcon from '@atlaskit/icon/core/data-string';
import DatabaseIcon from '@atlaskit/icon/core/database';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import ProjectionScreenIcon from '@atlaskit/icon/core/projection-screen';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { fg } from '@atlaskit/platform-feature-flags';

import messages from './messages';
import type { RemixOption, RemixSubType } from './types';

/**
 * All available remix options with their sub-types (variants).
 *
 * Sub-type values match the backend `dynamicUiSubtype` parameter
 * used in the remix-content minion (see edit-with-rovo-bar).
 */
const defaultInfographicSubTypes: RemixSubType[] = [
	{
		value: 'corporate-modern',
		displayName: 'modern style infographic',
		labelMessage: messages.subTypeModernStyle,
		testId: 'remix-sub-type-corporate-modern',
	},
	{
		value: 'corporate-doodle',
		displayName: 'sketch style infographic',
		labelMessage: messages.subTypeSketchStyle,
		testId: 'remix-sub-type-corporate-doodle',
	},
];

const newInfographicSubTypes: RemixSubType[] = [
	{
		value: 'charlie',
		displayName: 'bold style infographic',
		labelMessage: messages.subTypeBoldStyle,
		testId: 'remix-sub-type-charlie',
	},
	{
		value: 'minimalist',
		displayName: 'minimalist style infographic',
		labelMessage: messages.subTypeMinimalistStyle,
		testId: 'remix-sub-type-minimalist',
	},
	{
		value: 'corporate-doodle',
		displayName: 'sketch style infographic',
		labelMessage: messages.subTypeSketchStyle,
		testId: 'remix-sub-type-corporate-doodle',
	},
];

export function getInfographicSubTypes(): RemixSubType[] {
	return fg('confluence_infographics_enable_new_styles')
		? newInfographicSubTypes
		: defaultInfographicSubTypes;
}

export const REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'chart',
		displayName: 'chart',
		icon: ChartBarIcon,
		labelMessage: messages.remixOptionChart,
		iconAltMessage: messages.chartIconAlt,
		tooltipMessage: messages.chartTooltip,
		testId: 'remix-overlay-option-chart',
		subTypes: [
			{
				value: 'line-chart',
				displayName: 'line chart',
				labelMessage: messages.subTypeLineChart,
				testId: 'remix-sub-type-line-chart',
			},
			{
				value: 'area-chart',
				displayName: 'area chart',
				labelMessage: messages.subTypeAreaChart,
				testId: 'remix-sub-type-area-chart',
			},
			{
				value: 'bar-chart',
				displayName: 'bar chart',
				labelMessage: messages.subTypeBarChart,
				testId: 'remix-sub-type-bar-chart',
			},
			{
				value: 'funnel-chart',
				displayName: 'funnel chart',
				labelMessage: messages.subTypeFunnelChart,
				testId: 'remix-sub-type-funnel-chart',
			},
			{
				value: 'pie-chart',
				displayName: 'pie chart',
				labelMessage: messages.subTypePieChart,
				testId: 'remix-sub-type-pie-chart',
			},
			{
				value: 'gantt-chart',
				displayName: 'Gantt chart',
				labelMessage: messages.subTypeGanttChart,
				testId: 'remix-sub-type-gantt-chart',
			},
		],
	},
	{
		type: 'diagram',
		displayName: 'diagram',
		icon: DataFlowIcon,
		labelMessage: messages.remixOptionDiagram,
		iconAltMessage: messages.diagramIconAlt,
		tooltipMessage: messages.diagramTooltip,
		testId: 'remix-overlay-option-diagram',
		subTypes: [
			{
				value: 'mindmap',
				displayName: 'mind map',
				labelMessage: messages.subTypeMindMap,
				testId: 'remix-sub-type-mindmap',
			},
			{
				value: 'flowchart',
				displayName: 'flowchart',
				labelMessage: messages.subTypeFlowChart,
				testId: 'remix-sub-type-flowchart',
			},
		],
	},
	{
		type: 'infographic',
		displayName: 'infographic',
		icon: DataStringIcon,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
		subTypes: defaultInfographicSubTypes,
	},
	{
		type: 'visual-aid',
		displayName: 'visualization',
		icon: EyeOpenIcon,
		labelMessage: messages.remixOptionVisualization,
		iconAltMessage: messages.visualizationIconAlt,
		tooltipMessage: messages.visualizationTooltip,
		testId: 'remix-overlay-option-visual-aid',
		subTypes: [
			{
				value: 'flip-cards',
				displayName: 'flip card',
				labelMessage: messages.subTypeFlipCard,
				testId: 'remix-sub-type-flip-cards',
			},
			{
				value: 'quadrant',
				displayName: 'quadrant',
				labelMessage: messages.subTypeQuadrant,
				testId: 'remix-sub-type-quadrant',
			},
			{
				value: 'tree-map',
				displayName: 'tree map',
				labelMessage: messages.subTypeTreeMap,
				testId: 'remix-sub-type-tree-map',
			},
			{
				value: 'horizontal-timeline',
				displayName: 'timeline',
				labelMessage: messages.subTypeTimeline,
				testId: 'remix-sub-type-horizontal-timeline',
			},
			{
				value: 'map',
				displayName: 'map',
				labelMessage: messages.subTypeGeographicalMap,
				testId: 'remix-sub-type-map',
			},
			{
				value: 'org-chart',
				displayName: 'org chart',
				labelMessage: messages.subTypeOrgChart,
				testId: 'remix-sub-type-org-chart',
			},
		],
	},
];

/**
 * Page-level remix options shown from the ObjectSidebar entry point.
 * Uses the entire page ADF instead of selected content.
 *
 * Options with empty subTypes render as flat clickable rows;
 * options with subTypes render as expandable accordions.
 */
export const PAGE_REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'slides',
		displayName: 'slides',
		icon: ProjectionScreenIcon,
		labelMessage: messages.remixOptionSlides,
		iconAltMessage: messages.slidesIconAlt,
		tooltipMessage: messages.slidesTooltip,
		testId: 'remix-overlay-option-slides',
		subTypes: [],
		iconTileAppearance: 'green',
	},
	{
		type: 'whiteboard',
		displayName: 'whiteboard',
		icon: WhiteboardIcon,
		labelMessage: messages.remixOptionWhiteboard,
		iconAltMessage: messages.whiteboardIconAlt,
		tooltipMessage: messages.whiteboardTooltip,
		testId: 'remix-overlay-option-whiteboard',
		subTypes: [],
		iconTileAppearance: 'teal',
	},
	{
		type: 'database',
		displayName: 'database',
		icon: DatabaseIcon,
		labelMessage: messages.remixOptionDatabase,
		iconAltMessage: messages.databaseIconAlt,
		tooltipMessage: messages.databaseTooltip,
		testId: 'remix-overlay-option-database',
		subTypes: [],
		iconTileAppearance: 'purple',
	},
	{
		type: 'infographic',
		displayName: 'infographic',
		icon: DataStringIcon,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
		subTypes: defaultInfographicSubTypes,
	},
];

/**
 * Builds a combined list of all remix options with FG-gated infographic subtypes.
 * Called at render time to ensure feature flags are resolved.
 */
function getAllOptions(): RemixOption[] {
	const infographicSubTypes = getInfographicSubTypes();
	return [...REMIX_OPTIONS, ...PAGE_REMIX_OPTIONS].map((opt) =>
		opt.type === 'infographic' ? { ...opt, subTypes: infographicSubTypes } : opt,
	);
}

/**
 * Returns the human-readable display name for a type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getTypeDisplayName(typeValue: string): string {
	const match = getAllOptions().find((option) => option.type === typeValue);
	return match?.displayName ?? typeValue.replace(/-/g, ' ');
}

/**
 * Returns the human-readable display name for a sub-type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getSubTypeDisplayName(subTypeValue: string): string {
	for (const option of getAllOptions()) {
		const match = option.subTypes.find((st) => st.value === subTypeValue);
		if (match) {
			return match.displayName;
		}
	}
	return subTypeValue.replace(/-/g, ' ');
}
